import * as Sentry from "@sentry/react";
import { env } from "./env";

// A pay link's token is the payer's only credential, and Stripe's redirect
// back from a bank adds the PaymentIntent's client secret to the URL. Neither
// may reach Sentry, so every event, breadcrumb, transaction and span is
// redacted before it is sent.

// `pay` only as the first path segment (at the start, after a space or quote,
// or after an origin), so `/api/v5/checkout/pay` stays as it is.
const PAY_PAGE_TOKEN =
  /(^|[\s"'(]|[a-z][a-z\d+.-]*:\/\/[^/\s?#]+)\/pay\/[^/?#\s]+/gi;
// The browser's copy of platform/src/lib/telemetry/redact-request-path.ts,
// anywhere in a URL (with or without the platform's origin).
const PAY_LINK_API_TOKEN = /\/api\/v5\/pay-links\/[^/?#\s]+/g;
const QUERY_STRING = /\?[^#\s"'<>]*/g;
// Stripe's `payment_intent_client_secret` and `setup_intent_client_secret`,
// and the plain `client_secret` Stripe.js puts on its own requests.
const CLIENT_SECRET_PARAM = /^[\w.-]*client_secret=/i;
const BREADCRUMB_URL_KEYS = ["url", "from", "to"];

/** Pure. The text with pay-link tokens replaced by `:token` and client secrets removed. */
function redactTelemetryText(text) {
  return withoutClientSecrets(
    text
      .replace(PAY_PAGE_TOKEN, "$1/pay/:token")
      .replace(PAY_LINK_API_TOKEN, "/api/v5/pay-links/:token"),
  );
}

function withoutClientSecrets(text) {
  return text.replace(QUERY_STRING, (query) => {
    const params = query.slice(1).split("&");
    const kept = params.filter((param) => !CLIENT_SECRET_PARAM.test(param));
    if (kept.length === params.length) return query;
    return kept.length > 0 ? `?${kept.join("&")}` : "";
  });
}

function isPlainObject(value) {
  if (value === null || typeof value !== "object") return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/**
 * Pure. A copy of `value` with every string inside plain objects and arrays
 * redacted; anything else (a class instance such as a Sentry scope) is kept as
 * it is. `ancestors` stops a cycle: Sentry normalizes events, so none is
 * expected, but a walk that overflowed the stack would lose the event.
 */
function redactDeep(value, ancestors = new Set()) {
  if (typeof value === "string") return redactTelemetryText(value);
  if (!Array.isArray(value) && !isPlainObject(value)) return value;
  if (ancestors.has(value)) return value;
  const inner = new Set(ancestors).add(value);
  return Array.isArray(value)
    ? value.map((item) => redactDeep(item, inner))
    : Object.fromEntries(
        Object.entries(value).map(([key, item]) => [
          key,
          redactDeep(item, inner),
        ]),
      );
}

/**
 * Pure. Only the breadcrumb's message and URLs are redacted: a console
 * breadcrumb's `data.arguments` can hold any object of the page.
 */
function redactBreadcrumb(breadcrumb) {
  const { message, data } = breadcrumb;
  const urls = data
    ? Object.fromEntries(
        BREADCRUMB_URL_KEYS.filter((key) => typeof data[key] === "string").map(
          (key) => [key, redactTelemetryText(data[key])],
        ),
      )
    : {};
  return {
    ...breadcrumb,
    ...(typeof message === "string"
      ? { message: redactTelemetryText(message) }
      : {}),
    ...(data ? { data: { ...data, ...urls } } : {}),
  };
}

Sentry.init({
  dsn: env.PUBLIC_SENTRY_DSN,
  telemetry: false,
  enabled: env.PUBLIC_SENTRY_ENABLED,
  // Sentry 10.71 enables structured logs by default. Preserve the existing
  // error-and-trace-only behavior until the application adopts Sentry logs.
  enableLogs: false,

  // Reduce sample rate to prevent rate limiting (10% of traces)
  tracesSampleRate: env.PUBLIC_ENV === "development" ? 0.1 : 0.25,

  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [env.PUBLIC_PLATFORM_URL],

  integrations: [Sentry.browserTracingIntegration()],
  // Ignore common/noisy errors that don't need tracking
  ignoreErrors: [
    // Browser extensions and ResizeObserver
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    // Network errors (expected in normal operation)
    "NetworkError",
    "Network request failed",
    "Failed to fetch",
    // A route/dialog chunk the newer deployment replaced. `ErrorFallback`
    // reloads into the new build, so the user sees an update, not a failure.
    // Chrome's phrasing already matches "Failed to fetch" above.
    "error loading dynamically imported module",
    "Importing a module script failed",
    // Cancelled requests (user navigation)
    "AbortError",
    "cancelled",
    // Sentry's own errors
    "sentry.io",
    // TipTap DOM errors (race condition with React collaborative editing)
    "The object can not be found here",
    "NotFoundError",
    "insertBefore",
    "is not a child of this node",
  ],

  // Filter events before sending
  beforeSend(event, hint) {
    const redactedEvent = redactDeep(event);
    const error = hint.originalException;

    // Don't send if Sentry itself is rate limited
    if (error?.message?.includes("sentry.io")) {
      return null;
    }

    // Don't send in development mode
    if (env.PUBLIC_ENV === "development") {
      return null;
    }

    return redactedEvent;
  },

  // The transaction name, its request, contexts and spans.
  beforeSendTransaction(event) {
    return redactDeep(event);
  },

  // Also spans sent on their own, outside a transaction: an interaction span
  // carries the page's route name in `data.transaction`.
  beforeSendSpan(span) {
    return redactDeep(span);
  },

  beforeBreadcrumb(breadcrumb) {
    return redactBreadcrumb(breadcrumb);
  },
});
